import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Alert } from "../../components/Alert";
import { BillingStatusBadge } from "../../components/billing/BillingBadges";
import { Modal } from "../../components/crm/Modal";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import {
  createAccount,
  deleteAccount,
  isApiError,
  listAccounts,
  updateAccount,
} from "../../lib/billing-api";
import type { Account, AccountInput, AccountStatus } from "../../lib/billing-types";
import { formatDate, formatMoney, fromMajorInput, toMajorInput } from "../../lib/format";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

interface FormState {
  id: string | null;
  name: string;
  currency: string;
  status: AccountStatus;
  paymentTermDays: string;
  creditLimit: string;
  companyId: string;
  contactId: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  id: null,
  name: "",
  currency: "USD",
  status: "active",
  paymentTermDays: "",
  creditLimit: "",
  companyId: "",
  contactId: "",
  notes: "",
};

function AccountsInner(): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canManage = hasPermission("billing.account.manage");

  const [items, setItems] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const locale = lang === "es" ? "es-ES" : "en-US";

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const page = await listAccounts();
      setItems(page.items);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (form === null) {
      return;
    }
    setSaving(true);
    setError(null);
    const term = Number.parseInt(form.paymentTermDays, 10);
    const input: AccountInput = {
      name: form.name.trim(),
      currency: form.currency.trim().toUpperCase(),
      status: form.status,
      payment_term_days: Number.isNaN(term) ? null : term,
      credit_limit_minor: fromMajorInput(form.creditLimit),
      company_id: form.companyId.trim() === "" ? null : form.companyId.trim(),
      contact_id: form.contactId.trim() === "" ? null : form.contactId.trim(),
      notes: form.notes.trim() === "" ? null : form.notes.trim(),
    };
    try {
      if (form.id === null) {
        await createAccount(input);
      } else {
        await updateAccount(form.id, input);
      }
      setForm(null);
      await load();
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await deleteAccount(id);
      setConfirmId(null);
      await load();
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="page" aria-labelledby="billing-accounts-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="billing-accounts-title">{t("billing.accounts.title")}</h1>
          <p className="muted">{t("billing.accounts.subtitle")}</p>
        </div>
        {canManage ? (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setForm({ ...EMPTY_FORM })}
          >
            {t("billing.accounts.new")}
          </button>
        ) : null}
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items.length === 0 ? (
        <p className="empty">{t("billing.accounts.empty")}</p>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("billing.accounts.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("billing.account.name")}</th>
                <th scope="col">{t("billing.account.currency")}</th>
                <th scope="col">{t("billing.account.status")}</th>
                <th scope="col">{t("billing.account.creditLimit")}</th>
                <th scope="col">{t("billing.account.balance")}</th>
                <th scope="col">{t("billing.common.updated")}</th>
                {canManage ? (
                  <th scope="col" className="table__actions-col">
                    <span className="sr-only">{t("billing.common.actions")}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {items.map((account) => (
                <tr key={account.id}>
                  <td>{account.name}</td>
                  <td>{account.currency}</td>
                  <td>
                    <BillingStatusBadge status={account.status} />
                  </td>
                  <td>{formatMoney(account.credit_limit_minor, account.currency, locale)}</td>
                  <td>{formatMoney(account.balance_cached_minor, account.currency, locale)}</td>
                  <td>{formatDate(account.updated_at, locale)}</td>
                  {canManage ? (
                    <td className="table__actions-col">
                      <div className="inline-actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() =>
                            setForm({
                              id: account.id,
                              name: account.name,
                              currency: account.currency,
                              status: account.status as AccountStatus,
                              paymentTermDays:
                                account.payment_term_days === null
                                  ? ""
                                  : String(account.payment_term_days),
                              creditLimit: toMajorInput(account.credit_limit_minor),
                              companyId: account.company_id ?? "",
                              contactId: account.contact_id ?? "",
                              notes: account.notes ?? "",
                            })
                          }
                        >
                          {t("common.edit")}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setConfirmId(account.id)}
                        >
                          {t("billing.common.delete")}
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {form !== null ? (
        <Modal
          title={form.id === null ? t("billing.account.create") : t("billing.account.edit")}
          onClose={() => setForm(null)}
          busy={saving}
        >
          <form onSubmit={(event) => void onSubmit(event)}>
            <div className="form-grid">
              <label className="field" htmlFor="account-name">
                <span>{t("billing.account.name")}</span>
                <input
                  id="account-name"
                  value={form.name}
                  maxLength={200}
                  required
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="account-currency">
                <span>{t("billing.account.currency")}</span>
                <input
                  id="account-currency"
                  value={form.currency}
                  maxLength={3}
                  required
                  onChange={(event) => setForm({ ...form, currency: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="account-status">
                <span>{t("billing.account.status")}</span>
                <select
                  id="account-status"
                  value={form.status}
                  onChange={(event) =>
                    setForm({ ...form, status: event.target.value as AccountStatus })
                  }
                >
                  <option value="active">{t("billing.status.active")}</option>
                  <option value="blocked">{t("billing.status.blocked")}</option>
                  <option value="closed">{t("billing.status.closed")}</option>
                </select>
              </label>
              <label className="field" htmlFor="account-term">
                <span>{t("billing.account.paymentTerm")}</span>
                <input
                  id="account-term"
                  inputMode="numeric"
                  value={form.paymentTermDays}
                  onChange={(event) => setForm({ ...form, paymentTermDays: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="account-credit">
                <span>{t("billing.account.creditLimit")}</span>
                <input
                  id="account-credit"
                  inputMode="decimal"
                  value={form.creditLimit}
                  onChange={(event) => setForm({ ...form, creditLimit: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="account-company">
                <span>{t("billing.account.companyId")}</span>
                <input
                  id="account-company"
                  value={form.companyId}
                  onChange={(event) => setForm({ ...form, companyId: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="account-contact">
                <span>{t("billing.account.contactId")}</span>
                <input
                  id="account-contact"
                  value={form.contactId}
                  onChange={(event) => setForm({ ...form, contactId: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="account-notes">
                <span>{t("billing.common.notes")}</span>
                <textarea
                  id="account-notes"
                  value={form.notes}
                  rows={3}
                  maxLength={5000}
                  onChange={(event) => setForm({ ...form, notes: event.target.value })}
                />
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? t("common.saving") : t("common.save")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setForm(null)}>
                {t("common.cancel")}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {confirmId !== null ? (
        <Modal
          title={t("billing.account.deleteConfirm")}
          onClose={() => setConfirmId(null)}
          busy={saving}
          footer={
            <>
              <button
                type="button"
                className="btn btn--danger"
                disabled={saving}
                onClick={() => void onDelete(confirmId)}
              >
                {t("billing.common.delete")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmId(null)}>
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p>{t("billing.account.deleteConfirm")}</p>
        </Modal>
      ) : null}
    </section>
  );
}

export function AccountsPage(): ReactNode {
  return (
    <RequirePermission permission="billing.account.read">
      <AccountsInner />
    </RequirePermission>
  );
}
